import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { deleteRequest, getRequest, postRequest, tabulatorGet, downloadBlobGet, fetchTextGet } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Avatar } from 'primereact/avatar';
import { MdSearch, MdSearchOff } from "react-icons/md";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import {
  AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, SearchIcon,
  ExportIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon,
  ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon, RefreshIcon
} from "../svg/svgIcons";
import AuthContext from "../../components/AuthContext";
import { PassLogoutProvider } from '../../auth/passLogoutRef';
import { resolveUiLocale } from '../../utils/appUiLocale';
import { PanelMenu } from 'primereact/panelmenu';
import UtilContext from "../../components/UtilContext";
import { ensureMandatoryIdVisible } from '../../utils/maintenanceTabulatorMaskActions';
import { MaintenanceMaskDialogCheckbox } from '../../components/maintenance/MaintenanceMaskDialogCheckbox';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import { buildNavigationContextFromTable } from '../../utils/maintenanceNavigationContext';
import { useTabulatorScrollArrows } from '../../utils/useTabulatorScrollArrows';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { getEffectiveMask } from '../../utils/effectiveMask';
import { getTabulatorLayoutForStandardMask } from '../../utils/tabulatorLayoutUtils';
import { cleanSorters, extractSafePaginationParams, createDedupedAjaxRequest, refreshProgressiveFromStart, safeParse, applyUserMaskRowToTabulator, mergePagedStandardListFiltersAndSorters, sanitizeTabulatorLayoutAttributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS } from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { getPusherChannel } from '../../utils/pusherChannel';
import { dateUserHeaderFilter } from '../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth } from '../../utils/tabulatorColumnMinWidth';
import { buildTabulatorIdColumn } from '../../utils/tabulatorIdColumn';
import { DateTime } from 'luxon';
import { formatDate as formatDatePattern } from '../../utils/dateUtils';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import { LocaleContext } from '../../components/LocaleContext';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';
const MAINTENANCE_TYPE_LIST_COLUMN_I18N_KEYS = Object.freeze({
  'name': 'Common.Name',
  'type': 'Common.TaskType',
  'norm': 'Common.Norm',
  'machine_type.name': 'MachineList.Column.MachineTypeName',
  'interval_months': 'Common.IntervalMonths',
  'responsible.name': 'Common.ResponsibleShort',
  'use_central_responsible_person': 'MaintenanceTypeList.Text.Centralny_zodpovedny',
  'use_central_service_person': 'MaintenanceTypeList.Text.Centralna_servisna_firma_a_technik',
  'central_company.name': 'Maintenance.CentralServiceCompany',
  'central_lecturer_id': 'Maintenance.CentralTechnicianType',
  'central_company_contact_id': 'Maintenance.CentralServiceTechnician',
  'use_unified_template': 'MaintenanceTypeList.Text.Jednotna_predloha',
  'use_central_required_attachment': 'Maintenance.CentralRequiredAttachment',
  'state': 'Common.Status',
  'description': 'Common.Description',
  'created_at': 'Common.Column.CreatedAt',
  'updated_at': 'Common.Column.UpdatedAt',
  'rowSelection': 'Common.Name',
});


/** Viditeľnosť dátových stĺpcov zoznamu typov údržby (bez rowSelection / editor). */
const DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  name: true,
  type: true,
  norm: true,
  'machine_type.name': true,
  interval_months: true,
  'responsible.name': true,
  use_central_responsible_person: true,
  use_central_service_person: true,
  'central_company.name': true,
  central_lecturer_id: true,
  central_company_contact_id: true,
  use_unified_template: true,
  use_central_required_attachment: true,
  state: true,
  description: true,
  created_at: true,
  updated_at: true,
});
const MAINTENANCE_TYPE_XLSX_FILENAME = 'udrzba_typy_ukonov.xlsx';

function mergeMaintenanceTypeColumnVisibilityFromSaved(saved) {
  const modernMask = saved && typeof saved === 'object' && Object.prototype.hasOwnProperty.call(saved, 'id');
  const merged = {};

  for (const field of Object.keys(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY)) {
    if (saved && typeof saved === 'object' && Object.prototype.hasOwnProperty.call(saved, field)) {
      merged[field] = saved[field] !== false;
    } else if (modernMask) {
      merged[field] = false;
    } else {
      merged[field] = DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY[field];
    }
  }

  if (!Object.values(merged).some(Boolean)) {
    return ensureMandatoryIdVisible({ ...DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY }, DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY);
  }
  return ensureMandatoryIdVisible(merged, DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY);
}

function normalizeMaintenanceTypeColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY)) {
    out[field] = stateObj?.[field] !== false;
  }
  return ensureMandatoryIdVisible(out, DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY);
}

/** Štandardná maska: len dátové stĺpce (nie rowSelection / editor z Tabulatora). */
function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY)) {
    out[field] = raw?.[field] !== false;
  }
  return out;
}

/**
 * 1B: pri otvorení dialógu masky zobraz aktuálny stav, ale vždy maj kompletný objekt viditeľnosti
 * a nikdy „prázdnu“ masku (všetko false / chýbajúce kľúče → default zapnuté, prípadne celá default maska).
 */
function ensureMaintenanceTypeMaskDialogState(partial) {
  const out = {};
  for (const field of Object.keys(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY)) {
    if (!Object.prototype.hasOwnProperty.call(partial, field) || partial[field] === undefined || partial[field] === null) {
      out[field] = DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY[field];
    } else {
      out[field] = partial[field] !== false;
    }
  }
  if (!Object.values(out).some(Boolean)) {
    return ensureMandatoryIdVisible({ ...DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY }, DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY);
  }
  return ensureMandatoryIdVisible(out, DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY);
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try {
    table.setHeaderFilterValue?.(field, '', true);
  } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

/** Poradie stĺpcov pre tlač = aktuálne poradie v Tabulátore (maska), nie Object.keys defaultu. */
function getPrintColumnFieldOrderForMaintenanceTypeList(table, columnsVisible) {
  const allowed = Object.keys(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY);
  const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
  if (!table?.getColumns) {
    return fallback();
  }
  const allowedSet = new Set(allowed);
  const seen = new Set();
  const out = [];
  try {
    for (const col of table.getColumns()) {
      const field = col.getField?.() ?? col.getDefinition?.()?.field;
      if (!field || !allowedSet.has(field)) continue;
      if (columnsVisible[field] === false) continue;
      try {
        if (col.isVisible?.() === false) continue;
      } catch (_) {
        /* ignore */
      }
      if (seen.has(field)) continue;
      seen.add(field);
      out.push(field);
    }
  } catch (_) {
    return fallback();
  }
  return out.length > 0 ? out : fallback();
}

function countMaintenanceTypeDataColumnsVisible(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY).filter((k) => vis[k] !== false).length;
}

export default function MaintenanceTypeList({ preview = null }) {

  const [state, dispatch] = useContext(DatabaseContext);
  const { openFavoriteDialog, toast, setFastView, setUserMaskFilter, setStandardMaskFilter, setDialogText, setVisible, getDateFormat, getUserColor, getUserInitials , openChosenNotificationPreferences} = useContext(UtilContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [searchIconColor, setSearchIconColor] = useState("");
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  // Hodnota, ktorá sa skutočne posiela do backendu (po kliknutí na „Hľadaj“ / po vypnutí krížikom).
  const [appliedSearchValue, setAppliedSearchValue] = useState('');
  // Ref drží hodnotu pre backend synchronne (bez rizika, že React state sa stihne updatovať až po refresh).
  const submittedSearchValueRef = useRef('');
  const [columnsVisible, setColumnsVisible] = useState(() => ({ ...DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY }));
  const [maskVisible, setMaskVisible] = useState(false);

  const ref = useRef(null);
  const elementRef = useRef(null);
  const overlayRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const isRestoringRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find(s => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 50;
  }, [state.module_settings]);

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const refreshFromStart = useCallback(() => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.Loading')), [getTable]);

  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: MAINTENANCE_TYPE_LIST_COLUMN_I18N_KEYS,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    const apply = () => {
      holder.scrollLeft = left;
      if (header) header.scrollLeft = left;
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(apply, 50);
        setTimeout(() => {
          if (savedHScrollRef.current.pending) {
            apply();
            savedHScrollRef.current.pending = false;
          }
        }, 180);
      });
    });
  }, [getTable]);

  // Pusher: maintenance_type.created/updated/deleted – payload len { id }; frontend GET /api/maintenance_types/{id} a update/add/delete riadka.
  // Aplikujeme zmeny až po skončení aktívneho data load requestu, aby Tabulator neblokoval odpoveď (Data Load Response Blocked).
  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;
    const DEDUPE_MS = 800;
    const lastHandled = { key: '', at: 0 };
    const shouldSkip = (eventType, id) => {
      if (id == null) return true;
      const key = `${eventType}-${id}`;
      const now = Date.now();
      if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
      lastHandled.key = key;
      lastHandled.at = now;
      return false;
    };
    const applyWhenIdle = (fn) => {
      const p = inFlightRef.current?.promise;
      if (p) {
        p.finally(() => {
          try { fn(); } catch (_) {}
        });
      } else {
        try { fn(); } catch (_) {}
      }
    };
    const onCreated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('maintenance_type.created', id)) return;
      getRequest(`/api/maintenance_types/${id}`).then((res) => {
        const rowData = res?.data ?? res;
        if (!rowData?.id) return;
        applyWhenIdle(() => {
          const table = getTable();
          const row = table?.getRow?.(rowData.id);
          if (row) row.update(rowData);
          else table?.addData?.([rowData], true);
        });
      }).catch(() => {});
    };
    const onUpdated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('maintenance_type.updated', id)) return;
      getRequest(`/api/maintenance_types/${id}`).then((res) => {
        const rowData = res?.data ?? res;
        if (!rowData?.id) return;
        applyWhenIdle(() => {
          const table = getTable();
          const row = table?.getRow?.(rowData.id);
          if (row) row.update(rowData);
          else table?.addData?.([rowData], true);
        });
      }).catch(() => {});
    };
    const onDeleted = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('maintenance_type.deleted', id)) return;
      applyWhenIdle(() => {
        const table = getTable();
        const row = table?.getRow?.(id) ?? table?.getRows?.().find((r) => r.getData().id === id);
        if (row) row.delete();
      });
    };
    channel.bind('maintenance_type.created', onCreated);
    channel.bind('maintenance_type.updated', onUpdated);
    channel.bind('maintenance_type.deleted', onDeleted);
    return () => {
      channel.unbind('maintenance_type.created', onCreated);
      channel.unbind('maintenance_type.updated', onUpdated);
      channel.unbind('maintenance_type.deleted', onDeleted);
    };
  }, [getTable]);

  const buildRequestParams = useCallback((paramsFromTabulator = {}) => {
    const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
    if (extra.size == null) extra.size = pageSizePc;
    mergePagedStandardListFiltersAndSorters(extra, {
      getTable,
      tableBuiltForParams: tableBuiltRef.current,
      userMaskFilters: state.user_mask_filters,
      maskPage: 'MaintenanceTypeList',
    });
    const normalizedSearch = String(submittedSearchValueRef.current ?? '').trim();
    if (normalizedSearch !== '') extra.toolbar_search = normalizedSearch;
    return extra;
  }, [getTable, state.user_mask_filters, pageSizePc]);

  const buildMaintenanceTypeExportQueryString = useCallback((opts = {}) => {
    const { includePrintColumns = false, printLayout, printEngine, printOrientation } = opts;
    const extra = {};
    mergePagedStandardListFiltersAndSorters(extra, {
      getTable,
      tableBuiltForParams: tableBuiltRef.current,
      userMaskFilters: state.user_mask_filters,
      maskPage: 'MaintenanceTypeList',
    });
    const normalizedSearch = String(submittedSearchValueRef.current ?? '').trim();
    if (normalizedSearch !== '') extra.toolbar_search = normalizedSearch;
    if (includePrintColumns) {
      const order = getPrintColumnFieldOrderForMaintenanceTypeList(getTable(), columnsVisible);
      extra.print_columns = JSON.stringify(order);
      extra.print_layout = printLayout === 'minimal' ? 'minimal' : 'branded';
      if (printEngine === 'professional' || printEngine === 'browser') {
        extra.print_engine = printEngine;
      }
      if (printOrientation === 'portrait' || printOrientation === 'landscape') {
        extra.orientation = printOrientation;
      }
    }
    try {
      const loc = resolveUiLocale(PassLogoutProvider.user);
      if (loc && ['sk', 'en', 'de'].includes(loc)) {
        extra.locale = loc;
      }
    } catch (_) {
      /* ignore */
    }
    const qs = new URLSearchParams();
    Object.keys(extra).forEach((k) => {
      if (extra[k] !== undefined && extra[k] !== null) qs.set(k, String(extra[k]));
    });
    const q = qs.toString();
    return q ? `?${q}` : '';
  }, [getTable, state.user_mask_filters, columnsVisible]);

  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const runMaintenanceTypePrint = async (printLayout) => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildMaintenanceTypeExportQueryString({
        includePrintColumns: true,
        printLayout,
        printEngine: 'professional',
        printOrientation: printOrientationChoice,
      });
      const html = await fetchTextGet(`/api/maintenance_types/export/print${suffix}`);
      const w = window.open('', '_blank');
      if (!w) {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), life: 4000, detail: t('Common.EnablePopups') });
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      try {
        w.focus();
      } catch (_) {}
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Print'), life: 5000, detail: e?.message || t('Common.PrintFailed') });
    } finally {
      setVisible(false);
    }
  };

  const exportMaintenanceTypesXlsx = async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildMaintenanceTypeExportQueryString();
      const { blob, filename } = await downloadBlobGet(`/api/maintenance_types/export/xlsx${suffix}`, MAINTENANCE_TYPE_XLSX_FILENAME);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || MAINTENANCE_TYPE_XLSX_FILENAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast?.current?.show({ severity: 'info', summary: 'Hotovo', life: 3000, detail: t('Common.ExportDownloaded') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Export'), life: 5000, detail: e?.message || t('Common.ExportFailed') });
    } finally {
      setVisible(false);
    }
  };

  function searchTable() {
    const table = getTable();
    if (!table) return;
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setAppliedSearchValue(normalized);
    setSearchIconColor(normalized === '' ? '' : 'active');
    captureHScroll();
    refreshFromStart();
    setVisibleSearch(false);
  }

  const accept = () => {
    const table = getTable();
    const rows_to_delete = table?.getSelectedData?.() ?? [];
    rows_to_delete.forEach(element => {
      deleteRequest(`/api/maintenance_types/${element.id}`)
        .then(() => {
          toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordDeletedDetail') });
          captureHScroll();
          table?.replaceData?.();
        }).catch(() => {
          toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.DeleteFailedDetail'), life: 3000 });
        });
    });
  };

  const reject = () => {
    toast?.current?.show({ severity: 'warn', summary: t('Common.Cancelled'), detail: t('Common.Toast.RequestCancelled'), life: 3000 });
  };

  const confirm = () => {
    confirmDialog({
      message: t('Common.ConfirmDeleteRecord'),
      header: t('Common.ConfirmDelete'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept,
      reject
    });
  };

  const onSelectRow = (data) => {
    const count = Array.isArray(data) ? data.length : 0;
    const canEdit = user?.permissions?.includes('edit maintenance');
    const canAdd = user?.permissions?.includes('add maintenance');
    const canDelete = user?.permissions?.includes('delete maintenance');
    setEditIcon(!canEdit || count !== 1);
    setCopyIcon(!canAdd || count !== 1);
    setDeleteIcon(!canDelete || count < 1);
  };

  const normalizeText = (value) => (value ?? "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  function textHeaderFilter(headerValue, rowValue, rowData, filterParams) {
    if (!headerValue) return true; // prázdny filter = všetko
    return normalizeText(rowValue).includes(normalizeText(headerValue));
  }

  function onEditRow(mt) {
    const navigationContext = buildNavigationContextFromTable({ table: getTable(), cleanSorters });
    dispatch({
      type: 'create_new_tab', name: t('Util.Tab.EditMaintenanceType'), translationKey: 'Util.Tab.EditMaintenanceType', legacyLabel: t('Util.Tab.EditMaintenanceType'),
      page: 'MaintenanceTypeAdd',
      props: { maintenance_type: mt, type: 'edit', navigationContext },
      mask: 'mntn002'
    });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();

    return (
      <div className='flex items-center justify-center w-full h-full'>
        <button type='button' onClick={() => onEditRow(rowData)}><EditRowIcon /></button>
      </div>
    );
  }

  function StateFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const state = rowData?.state == null || rowData?.state === '' ? 'Aktívny' : rowData.state;
    return <span className={`block w-full min-w-0 truncate whitespace-nowrap ${state === "Zrušený" ? 'text-red-600' : ''}`} title={state}>{state}</span>;
  }

  const dateFormatGlob = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFormatGlob = `${dateFormatGlob} HH:mm`;

  function CreatedAtFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const createdBy = rowData?.created_by_user;
    const val = rowData?.created_at;
    const displayName = createdBy?.employee?.name || createdBy?.username || '';
    const dt = val ? DateTime.fromISO(val) : null;
    const text = val && dt?.isValid ? formatDatePattern(val, dateFormatGlob, true) : (val || '');
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {createdBy && (
          <Avatar
            className="shrink-0"
            title={displayName}
            label={getUserInitials?.(displayName) || ''}
            shape="circle"
            style={{ backgroundColor: getUserColor?.(createdBy.username) || '#6b7280', color: '#ffffff' }}
          />
        )}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const updatedBy = rowData?.updated_by_user;
    const val = rowData?.updated_at;
    const displayName = updatedBy?.employee?.name || updatedBy?.username || '';
    const dt = val ? DateTime.fromISO(val) : null;
    const text = val && dt?.isValid ? formatDatePattern(val, dateFormatGlob, true) : (val || '');
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {updatedBy && (
          <Avatar
            className="shrink-0"
            title={displayName}
            label={getUserInitials?.(displayName) || ''}
            shape="circle"
            style={{ backgroundColor: getUserColor?.(updatedBy.username) || '#6b7280', color: '#ffffff' }}
          />
        )}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function dateColumnFilter(headerValue, rowValue, rowData, filterParams) {
    if (!headerValue || String(headerValue).trim() === '') return true;
    if (String(headerValue).startsWith('range:')) return true;
    const normalize = (str) => (str || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const search = normalize(headerValue);
    const name = filterParams?.name;
    if (name === 'created_at' && rowData?.created_by_user) {
      const userName = rowData.created_by_user?.employee?.name || rowData.created_by_user?.username || '';
      if (normalize(userName).includes(search)) return true;
    }
    if (name === 'updated_at' && rowData?.updated_by_user) {
      const userName = rowData.updated_by_user?.employee?.name || rowData.updated_by_user?.username || '';
      if (normalize(userName).includes(search)) return true;
    }
    const field = name === 'created_at' ? 'created_at' : 'updated_at';
    const raw = rowData?.[field];
    if (!raw) return false;
    const dt = DateTime.fromISO(raw);
    const variants = dt.isValid
      ? [dt.toFormat(dateFormatGlob), dt.toFormat(dateTimeFormatGlob), dt.toFormat('yyyy-MM-dd')].join(' ')
      : '';
    return normalize(variants).includes(search);
  }

  function stateHeaderFilter(headerValue, rowValue, rowData) {
    const state = rowData?.state == null || rowData?.state === '' ? 'Aktívny' : rowData.state;
    if (!headerValue || String(headerValue).trim() === '') return true;
    const selected = String(headerValue).split(',').map(s => s.trim()).filter(Boolean);
    return selected.length > 0 && selected.includes(state);
  }

  function YesNoFormatter(props) {
    const val = props.cell.getValue();
    const b = val === true || val === 1 || val === '1';
    return <span>{b ? t('Common.Yes') : t('Common.No')}</span>;
  }
  function TruncateSpan({ text }) {
    const t = (text ?? '').toString();
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={t || undefined}>{t}</span>;
  }
  function TruncateCellFormatter(props) {
    return <TruncateSpan text={props.cell.getValue()} />;
  }
  function booleanHeaderFilter(headerValue, rowValue, rowData, filterParams) {
    const field = filterParams?.name;
    if (!field || !headerValue || String(headerValue).trim() === '') return true;
    const selected = new Set(String(headerValue).split(',').map(s => s.trim()).filter(Boolean));
    const raw = rowData?.[field];
    const b = raw === true || raw === 1 || raw === '1';
    const label = b ? t('Common.Yes') : t('Common.No');
    return selected.has(label);
  }
  function CentralCompanyFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const name = rowData?.central_company?.name ?? '';
    return <TruncateSpan text={name} />;
  }
  function CentralLecturerFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const lid = rowData?.central_lecturer_id;
    if (lid == null || lid === '') return <span />;
    return <span>{Number(lid) === 1 ? t('Common.InternalEmployee') : t('Common.ServiceCompanyEmployee')}</span>;
  }
  function CentralContactFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const cc = rowData?.central_company_contact;
    const name = cc?.employee?.name ?? cc?.name ?? '';
    return <TruncateSpan text={name} />;
  }

  const [columns, setColumns] = useState([
    { title: '', field: 'rowSelection', formatter: "rowSelection", titleFormatter: "rowSelection", hozAlign: "center", width: 44, headerSort: false, resizable: false, frozen: true, print:false, download:false },
    { title: '', field: 'editor', hozAlign: "center", width: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print:false, download:false },
    buildTabulatorIdColumn({ frozen: true, headerFilterFunc: textHeaderFilter }),
    { title: t('Common.Name'), field: 'name', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc:textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Name')), widthGrow: 1, hozAlign: 'left', formatter: reactFormatter(<TruncateCellFormatter />) },
    { title: t('Common.TaskType'), field: 'type', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc:textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(t('Common.TaskType')), widthGrow: 1, hozAlign: 'left', formatter: reactFormatter(<TruncateCellFormatter />) },
    { title: t('Common.Norm'), field: 'norm', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc:textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Norm')), widthGrow: 1, hozAlign: 'left', formatter: reactFormatter(<TruncateCellFormatter />) },
    { title: t('MachineList.Column.MachineTypeName'), field: 'machine_type.name', minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.MachineTypeName'), 'multiselect'), widthGrow: 1, hozAlign: 'left', formatter: reactFormatter(<TruncateCellFormatter />),
    headerFilter: primeMultiSelectHeaderFilter,
    headerFilterParams: { placeholder: t('Common.FilterAll'), valueType: 'string', options: (state.machine_types || []).map(mt => ({ label: mt.name, value: mt.name })) },
    headerFilterFunc: (headerValue, rowValue) => {
      if (!headerValue) return true;
      const selected = new Set(String(headerValue).split(',').map((x) => x.trim()).filter(Boolean));
      return selected.has(String(rowValue ?? ''));
    }
  },
    { title: t('Common.IntervalMonths'), field: 'interval_months', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc:textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(t('Common.IntervalMonths')), widthGrow: 1, hozAlign: 'left', sorter: 'number' },
    { title: t('Common.ResponsibleShort'), field: 'responsible.name', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc:textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(t('Common.ResponsibleShort')), widthGrow: 1, hozAlign: 'left', formatter: reactFormatter(<TruncateCellFormatter />) },
    { title: t('MaintenanceTypeList.Text.Centralny_zodpovedny'), field: 'use_central_responsible_person', minWidth: tabulatorColumnMinWidthFromTitle(t('MaintenanceTypeList.Text.Centralny_zodpovedny'), 'multiselect'), widthGrow: 0.5, formatter: reactFormatter(<YesNoFormatter />), headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { placeholder: t('Common.FilterAll'), options: [{ label: t('Common.Yes'), value: "Áno" }, { label: t('Common.No'), value: "Nie" }] }, headerFilterFunc: (hv, rv, rowData) => booleanHeaderFilter(hv, rv, rowData, { name: 'use_central_responsible_person' }), hozAlign: 'left', sorter: 'number' },
    { title: t('MaintenanceTypeList.Text.Centralna_servisna_firma_a_technik'), field: 'use_central_service_person', minWidth: tabulatorColumnMinWidthFromTitle(t('MaintenanceTypeList.Text.Centralna_servisna_firma_a_technik'), 'multiselect'), widthGrow: 0.5, formatter: reactFormatter(<YesNoFormatter />), headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { placeholder: t('Common.FilterAll'), options: [{ label: t('Common.Yes'), value: "Áno" }, { label: t('Common.No'), value: "Nie" }] }, headerFilterFunc: (hv, rv, rowData) => booleanHeaderFilter(hv, rv, rowData, { name: 'use_central_service_person' }), hozAlign: 'left', sorter: 'number' },
    { title: t('Maintenance.CentralServiceCompany'), field: 'central_company.name', minWidth: tabulatorColumnMinWidthFromTitle(t('Maintenance.CentralServiceCompany')), widthGrow: 1, formatter: reactFormatter(<CentralCompanyFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: (hv, rv, rowData) => { if (!hv || !hv.trim()) return true; const n = (rowData?.central_company?.name ?? '').toString(); return textHeaderFilter(hv, n, rowData); }, hozAlign: 'left', sorter: 'string' },
    { title: t('Maintenance.CentralTechnicianType'), field: 'central_lecturer_id', minWidth: tabulatorColumnMinWidthFromTitle(t('Maintenance.CentralTechnicianType'), 'multiselect'), widthGrow: 0.8, formatter: reactFormatter(<CentralLecturerFormatter />), headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { placeholder: t('Common.FilterAll'), options: [{ label: t('Common.InternalEmployee'), value: '1' }, { label: t('Common.ServiceCompanyEmployee'), value: '0' }] }, headerFilterFunc: (headerValue, rowValue) => { if (!headerValue || String(headerValue).trim() === '') return true; const selected = new Set(String(headerValue).split(',').map((x) => x.trim()).filter(Boolean)); return selected.has(String(rowValue ?? '')); }, hozAlign: 'left', sorter: 'number' },
    { title: t('Maintenance.CentralServiceTechnician'), field: 'central_company_contact_id', minWidth: tabulatorColumnMinWidthFromTitle(t('Maintenance.CentralServiceTechnician')), widthGrow: 1, formatter: reactFormatter(<CentralContactFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: (hv, rv, rowData) => { if (!hv || !hv.trim()) return true; const cc = rowData?.central_company_contact; const n = (cc?.employee?.name ?? cc?.name ?? '').toString(); return textHeaderFilter(hv, n, rowData); }, hozAlign: 'left', sorter: 'string' },
    { title: t('MaintenanceTypeList.Text.Jednotna_predloha'), field: 'use_unified_template', minWidth: tabulatorColumnMinWidthFromTitle(t('MaintenanceTypeList.Text.Jednotna_predloha'), 'multiselect'), widthGrow: 0.5, formatter: reactFormatter(<YesNoFormatter />), headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { placeholder: t('Common.FilterAll'), options: [{ label: t('Common.Yes'), value: "Áno" }, { label: t('Common.No'), value: "Nie" }] }, headerFilterFunc: (hv, rv, rowData) => booleanHeaderFilter(hv, rv, rowData, { name: 'use_unified_template' }), hozAlign: 'left', sorter: 'number' },
    { title: t('Maintenance.CentralRequiredAttachment'), field: 'use_central_required_attachment', minWidth: tabulatorColumnMinWidthFromTitle(t('Maintenance.CentralRequiredAttachment'), 'multiselect'), widthGrow: 0.6, formatter: reactFormatter(<YesNoFormatter />), headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { placeholder: t('Common.FilterAll'), options: [{ label: t('Common.Yes'), value: "Áno" }, { label: t('Common.No'), value: "Nie" }] }, headerFilterFunc: (hv, rv, rowData) => booleanHeaderFilter(hv, rv, rowData, { name: 'use_central_required_attachment' }), hozAlign: 'left', sorter: 'number' },
    { title: t('Common.Status'), field: 'state', headerFilter: primeMultiSelectHeaderFilter, headerFilterPlaceholder: t('Common.FilterAll'), headerFilterParams: { placeholder: t('Common.FilterAll'), valueType: 'string', options: [{ label: t('Common.Active'), value: 'Aktívny' }, { label: t('TrainingTypeList.Status.Cancelled'), value: "Zrušený" }] }, headerFilterFunc: stateHeaderFilter, formatter: reactFormatter(<StateFormatter />), minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Status'), 'multiselect'), widthGrow: 1, hozAlign: 'left' },
    { title: t('Common.Description'), field: 'description', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc:textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Description')), widthGrow: 1, hozAlign: 'left', formatter: reactFormatter(<TruncateCellFormatter />) },
    { title: t('Common.Column.CreatedAt'), field: 'created_at', minWidth: tabulatorDateUserColumnMinWidth(t('Common.Column.CreatedAt')), formatter: reactFormatter(<CreatedAtFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFormatGlob, dateTimeFormat: dateTimeFormatGlob }, headerFilterFunc: dateColumnFilter, headerFilterFuncParams: { name: 'created_at' }, hozAlign: 'left', sorter: 'datetime', sorterParams: { format: 'iso' } },
    { title: t('Common.Column.UpdatedAt'), field: 'updated_at', minWidth: tabulatorDateUserColumnMinWidth(t('Common.Column.UpdatedAt')), formatter: reactFormatter(<UpdatedAtFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFormatGlob, dateTimeFormat: dateTimeFormatGlob }, headerFilterFunc: dateColumnFilter, headerFilterFuncParams: { name: 'updated_at' }, hozAlign: 'left', sorter: 'datetime', sorterParams: { format: 'iso' } },
  ]);

  // Rebuild chrome titles/placeholders when locale or machine-type options change (EXPECTED_REMOUNT / fresh open).
  useEffect(() => {
    setColumns((prev) => {
      const options = (state.machine_types || []).map((mt) => ({ label: mt.name, value: mt.name }));
      const next = prev.map((col) => {
        if (col.field === 'norm') return { ...col, title: t('Common.Norm'), minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Norm')) };
        if (col.field === 'interval_months') return { ...col, title: t('Common.IntervalMonths'), minWidth: tabulatorColumnMinWidthFromTitle(t('Common.IntervalMonths')) };
        if (col.field === 'machine_type.name') {
          return {
            ...col,
            title: t('MachineList.Column.MachineTypeName'),
            minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.MachineTypeName'), 'multiselect'),
            headerFilterParams: { placeholder: t('Common.FilterAll'), valueType: 'string', options },
          };
        }
        if (col.headerFilter === primeMultiSelectHeaderFilter || col.headerFilterParams?.placeholder != null) {
          const params = { ...(col.headerFilterParams || {}), placeholder: t('Common.FilterAll') };
          if (col.field === 'state') {
            params.options = [{ label: t('Common.Active'), value: 'Aktívny' }, { label: t('TrainingTypeList.Status.Cancelled'), value: 'Zrušený' }];
          } else if (Array.isArray(params.options)) {
            params.options = params.options.map((o) => {
              if (o?.value === 'Áno') return { ...o, label: t('Common.Yes') };
              if (o?.value === 'Nie') return { ...o, label: t('Common.No') };
              if (o?.value === '1' && col.field === 'central_lecturer_id') return { ...o, label: t('Common.InternalEmployee') };
              if (o?.value === '0' && col.field === 'central_lecturer_id') return { ...o, label: t('Common.ServiceCompanyEmployee') };
              return o;
            });
          }
          const titleKeys = {
            use_central_responsible_person: 'MaintenanceTypeList.Text.Centralny_zodpovedny',
            use_central_service_person: 'MaintenanceTypeList.Text.Centralna_servisna_firma_a_technik',
            central_lecturer_id: 'Maintenance.CentralTechnicianType',
            use_unified_template: 'MaintenanceTypeList.Text.Jednotna_predloha',
            use_central_required_attachment: 'Maintenance.CentralRequiredAttachment',
            state: 'Common.Status',
          };
          const titleKey = titleKeys[col.field];
          return {
            ...col,
            ...(titleKey ? { title: t(titleKey), minWidth: tabulatorColumnMinWidthFromTitle(t(titleKey), 'multiselect') } : {}),
            headerFilterPlaceholder: t('Common.FilterAll'),
            headerFilterParams: params,
          };
        }
        return col;
      });
      if (ref.current?.setColumns) setTimeout(() => ref.current.setColumns(next), 0);
      return next;
    });
  }, [locale, t, state.machine_types]);

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useTabulatorScrollArrows(getTable);

  function addNewType() {
    dispatch({
      type: 'create_new_tab', name: t('Maintenance.NewType'), translationKey: 'Maintenance.NewType', legacyLabel: t('Maintenance.NewType'),
      page: 'MaintenanceTypeAdd',
      props: { maintenance_type: null, type: 'add' },
      mask: 'mntn002'
    });
  }

  function copyType() {
    const sel = getTable()?.getSelectedData?.()?.[0];
    const navigationContext = buildNavigationContextFromTable({ table: getTable(), cleanSorters });
    dispatch({
      type: 'create_new_tab', name: t('Maintenance.NewType'), translationKey: 'Maintenance.NewType', legacyLabel: t('Maintenance.NewType'),
      page: 'MaintenanceTypeAdd',
      props: { maintenance_type: sel, type: 'add', navigationContext },
      mask: 'mntn002'
    });
  }

  function editType() {
    const sel = getTable()?.getSelectedData?.()?.[0];
    const navigationContext = buildNavigationContextFromTable({ table: getTable(), cleanSorters });
    dispatch({
      type: 'create_new_tab', name: t('Util.Tab.EditMaintenanceType'), translationKey: 'Util.Tab.EditMaintenanceType', legacyLabel: t('Util.Tab.EditMaintenanceType'),
      page: 'MaintenanceTypeAdd',
      props: { maintenance_type: sel, type: 'edit', navigationContext },
      mask: 'mntn002'
    });
  }

  // MASKA – mení len dátové stĺpce definované v DEFAULT (nie rowSelection / editor).
  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY, ...columnsVisible, [field]: value };
    if (value === false && countMaintenanceTypeDataColumnsVisible(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: 'Maska', detail: t('Common.AtLeastOneColumnVisible'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY };
    for (const k of Object.keys(next)) next[k] = true;
    const table = getTable();
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    for (const k of Object.keys(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY)) next[k] = false;
    next.id = true;
    const table = getTable();
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  // MENU
  const itemRenderer = (item, options) => (
    <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </a>
  );

  const [expandedKeys, setExpandedKeys] = useState({});
  const closeMenu = () => setTimeout(() => { OverflowMenuProvider.setVisible?.(null); setExpandedKeys({}); }, 0);

  const handleClickOutside = (event) => {
    if (event.target.closest('[data-overflow-trigger]')) return;
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
      OverflowMenuProvider.setVisible?.(null);
    }
  };
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const canSetStandardMask = user?.permissions?.includes('set standard mask');

  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === 'MaintenanceTypeList');
    if (!row) return false;
    const attributes = safeParse(row.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeMaintenanceTypeColumnVisibilityFromSaved(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const openMaskDialog = () => {
    const merged = mergeMaintenanceTypeColumnVisibilityFromSaved(columnsVisible);
    setColumnsVisible(ensureMaintenanceTypeMaskDialogState(merged));
    setMaskVisible(true);
    closeMenu();
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizeMaintenanceTypeColumnVisibilityForSave(columnsVisible);
    const anyVisible = Object.values(normalized).some(Boolean);
    if (!anyVisible) {
      toast?.current?.show({ severity: 'warn', summary: 'Maska', detail: t('Common.SelectAtLeastOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = getTable()?.getHeaderFilters?.() ?? [];
    setUserMaskFilter('MaintenanceTypeList', 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message:
        t('Common.SaveStandardMaskMessage'),
      header: t('Common.SaveStandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) {
          toast?.current?.show({ severity: 'warn', summary: 'Maska', detail: t('Common.TableNotReady'), life: 4000 });
          return;
        }
        const columnsForSave = columnsFromTabulatorForStandardMaskSave(layout.columns);
        if (!Object.values(columnsForSave).some(Boolean)) {
          toast?.current?.show({ severity: 'warn', summary: t('Common.StandardMask'), detail: t('ServiceTask.AtLeastOneColumnVisible'), life: 4000 });
          return;
        }
        setStandardMaskFilter('MaintenanceTypeList', columnsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === 'MaintenanceTypeList');
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('Common.StandardMask'), detail: t('Common.StandardMaskMissing'), life: 4000 });
      return;
    }
    const attributes = safeParse(standard.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) {
      toast?.current?.show({
        severity: 'warn',
        summary: t('Common.StandardMask'),
        detail: t('Common.StandardMaskInvalidLayout'),
        life: 5000,
      });
      return;
    }
    const jsonColumns = safeParse(standard.columns, {});
    const mergedCols = mergeMaintenanceTypeColumnVisibilityFromSaved(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeMaintenanceTypeColumnVisibilityForSave(mergedCols);
    if (!Object.values(colsForSave).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('Common.StandardMask'), detail: t('Common.StandardMaskNoVisibleColumn'), life: 4000 });
      return;
    }
    confirmDialog({
      message:
        t('Common.ApplyStandardMaskMessage'),
      header: t('Common.StandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter('MaintenanceTypeList', 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try {
            table.setColumnLayout?.(sanitizeTabulatorLayoutAttributes(attributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS));
          } catch (_) {}
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => {
            isRestoringRef.current = false;
            try {
              table.redraw?.(true);
            } catch (_) {}
            captureHScroll();
            refreshFromStart();
          }, 0);
        });
      },
    });
  };

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: [
        {
          key: '0_1',
          label: t('Common.SetFilter'),
          icon: 'pi pi-filter',
          command: () => { setUserMaskFilter("MaintenanceTypeList", "set_filters", null, getTable()?.getHeaderFilters?.() ?? [], null); captureHScroll(); refreshFromStart(); closeMenu(); },
          template: itemRenderer,
        },
        {
          key: '0_2',
          label: t('Common.ClearFilter'),
          icon: 'pi pi-filter-slash',
          command: () => { setUserMaskFilter("MaintenanceTypeList","set_filters",null,[],null); getTable()?.clearHeaderFilter?.(); captureHScroll(); refreshFromStart(); closeMenu(); },
          template: itemRenderer,
        },
        {
          key: '0_3',
          label: t('Common.SetSorting'),
          icon: 'pi pi-sort-alt',
          command: () => { setUserMaskFilter("MaintenanceTypeList","set_sorters",null,null,null,cleanSorters(getTable()?.getSorters?.() ?? [])); captureHScroll(); refreshFromStart(); closeMenu(); },
          template: itemRenderer,
        },
        {
          key: '0_4',
          label: t('Common.ClearSorting'),
          icon: 'pi pi-sort-alt-slash',
          command: () => { setUserMaskFilter("MaintenanceTypeList","set_sorters",null,null,null,[]); getTable()?.setSort?.([]); captureHScroll(); refreshFromStart(); closeMenu(); },
          template: itemRenderer,
        },
        {
          key: '0_5',
          label: t('Common.SetMask'),
          icon: 'pi pi-server',
          command: openMaskDialog,
          template: itemRenderer,
        },
        ...(hasApplicableStandardMask
          ? [
              {
                key: '0_5s',
                label: t('Common.StandardMask'),
                icon: 'pi pi-clone',
                command: confirmApplyStandardLayoutToUserMask,
                template: itemRenderer,
              },
            ]
          : []),
        {
          key: '0_6',
          label: t('Common.Notifications'),
          icon: 'pi pi-envelope',
          command: () => { openChosenNotificationPreferences('MaintenanceTypeList'); closeMenu(); },
          template: itemRenderer,
        },
      ]
    }
  ];

  return (
    <>
      <div className='w-full h-10 flex flex-row bg-[#333]'>
        <IntersectionObserverWrapper>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="add"><button onClick={addNewType}><AddIcon /></button></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="edit"><button className="disabled:opacity-25" disabled={editIcon} onClick={editType}><EditIcon /></button></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="copy"><button className="disabled:opacity-25" disabled={copyIcon} onClick={copyType}><CopyIcon /></button></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="delete"><button className="disabled:opacity-25" disabled={deleteIcon} onClick={confirm}><DeleteIcon /></button></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="print"><button type="button" title={t('Common.Print')} onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="search">
            <button
              onClick={
                searchIconColor !== "active"
                  ? () => setVisibleSearch(true)
                  : () => {
                      setSearchIconColor("");
                      setSearchValue("");
                      setAppliedSearchValue("");
                      submittedSearchValueRef.current = '';
                      setVisibleSearch(false);
                      captureHScroll();
                      refreshFromStart();
                    }
              }
            >
              {searchIconColor === "" ? (
                <MdSearch fill="#fff" className='w-[28px] h-[28px]' aria-hidden="true" title={t('Common.Search')} />
              ) : (
                <MdSearchOff fill="#fff" className='w-[28px] h-[28px]' aria-hidden="true" title={t('ServiceTask.CancelSearch')} />
              )}
            </button>
          </div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="export"><button type="button" onClick={() => exportMaintenanceTypesXlsx()} title={t('Common.ExportToExcel')}><ExportIcon /></button></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="refresh"><button type="button" onClick={() => { captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}><RefreshIcon /></button></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon /></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="first"><button onClick={() => scrollToFirst()}><BackToFirstIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="back"><button onClick={() => scrollUp()}><BackIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="forward"><button onClick={() => scrollDown()}><ForwardIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="last"><button onClick={() => scrollToLast()}><ForwardToLastIcon/></button></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="add"><VerticalLineIcon /></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="add"><button onClick={() => openFavoriteDialog(t('NotificationTopic.maintenance.types'), "MaintenanceTypeList")}><FavoriteIcon /></button></div>
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="add"><button onClick={() => setFastView("MaintenanceTypeList", columnsVisible, columns)}><PriceIcon /></button></div>
          {canSetStandardMask ? (
            <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('Common.SaveStandardMask')}
                aria-label={t('Common.SaveStandardMask')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className='flex w-10 h-10 px-2 py-1 justify-center' data-targetid="add"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem"/>
          </div>
        </IntersectionObserverWrapper>
      </div>

      <div className='px-1 bg-white rounded-md shadow-md'>
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <div ref={elementRef}>
                <ReactTabulator
                  className='h-[calc(100vh_-_193px)]'
                  onRef={(r) => (ref.current = r.current)}
                  columns={columns}
                  events={{
                    rowSelectionChanged: onSelectRow,
                    tableBuilt: () => {
                      const table = getTable();
                      if (!table) return;
                      const effective = getEffectiveMask('MaintenanceTypeList', state);
                      if (effective?.type === 'user' && effective.userMask) {
                        const m = effective.userMask;
                        isRestoringRef.current = true;
                        applyUserMaskRowToTabulator(table, m, {
                          setColumnsVisible,
                          clearHeaderFiltersWhenEmpty: true,
                          forceFrozenId: true,
                          applyColumnVisibilityFn: (tbl, jc, setVis) => {
                            const merged = mergeMaintenanceTypeColumnVisibilityFromSaved(jc);
                            setVis(merged);
                            applyDataColumnVisibilityToTable(tbl, merged);
                          },
                        });
                        {
                          const merged = mergeMaintenanceTypeColumnVisibilityFromSaved(safeParse(m.columns, {}));
                          for (const field of Object.keys(DEFAULT_MAINTENANCE_TYPE_COLUMN_VISIBILITY)) {
                            if (merged[field] === false) clearHeaderFilterForField(table, field);
                          }
                        }
                        setTimeout(() => { isRestoringRef.current = false; }, 0);
                      } else if (effective?.type === 'standard' && effective.standardMask) {
                        const m = effective.standardMask;
                        isRestoringRef.current = true;
                        const attributes = safeParse(m.attributes, []);
                        const json_columns = safeParse(m.columns, {});
                        if (Array.isArray(attributes) && attributes.length > 1) {
                          table.setColumnLayout?.(sanitizeTabulatorLayoutAttributes(attributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS));
                        }
                        if (json_columns && typeof json_columns === "object") {
                          const merged = mergeMaintenanceTypeColumnVisibilityFromSaved(json_columns);
                          setColumnsVisible(merged);
                          applyDataColumnVisibilityToTable(table, merged);
                        }
                        setTimeout(() => { isRestoringRef.current = false; }, 0);
                      }
                      tableBuiltRef.current = true;
                    },
                    ...getTabulatorPersistLayoutEvents("MaintenanceTypeList", ref, setUserMaskFilter),
                    headerFilterChanged: () => { if (!isRestoringRef.current) captureHScroll(); },
                    sortChanged: () => { if (!isRestoringRef.current) captureHScroll(); },
                    renderComplete: () => restoreHScroll(),
                  }}
                  options={{
                    layout: "fitColumns",
                    autoResize: true,
                    renderHorizontal: "virtual",
                    rowHeight: 42,
                    movableColumns: true,
                    headerSortTristate: true,
                    ajaxURL: "/api/maintenance_types/paged",
                    ajaxParams: () => ({}),
                    ajaxRequestFunc: createDedupedAjaxRequest(buildRequestParams, tabulatorGet, inFlightRef, completedCacheRef),
                    dataLoader: true,
                    dataLoaderLoading: t('Common.Loading'),
                    dataLoaderError: t('Common.LoadFailed'),
                    progressiveLoad: "scroll",
                    progressiveLoadScrollMargin: 80,
                    paginationSize: pageSizePc,
                    filterMode: "remote",
                    sortMode: "remote",
                    pagination: false,
                    ajaxRequesting: () => {
                      captureHScroll();
                      const table = getTable();
                      if (table?.initialized) table.alert?.(t('Common.Loading'));
                      return true;
                    },
                    ajaxResponse: (url, params, response) => {
                      getTable()?.clearAlert?.();
                      return response;
                    },
                    ajaxError: () => {
                      getTable()?.clearAlert?.();
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <PrintOptionsDialog
          visible={printChoiceVisible}
          onHide={() => setPrintChoiceVisible(false)}
          orientation={printOrientationChoice}
          onOrientationChange={setPrintOrientationChoice}
          layout={printLayoutChoice}
          onLayoutChange={setPrintLayoutChoice}
          onConfirm={() => runMaintenanceTypePrint(printLayoutChoice)}
        />

        {/* HĽADANIE */}
        <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
          <div className="flex flex-col">
            <p className="text-sm text-gray-600 mb-2">{t('Ui.R67.Hlada_sa_globalne_v_niekolkych_stlpcoch_diakritika_sa_pri_po')}</p>
            <div className="flex items-center gap-3 w-full">
              <input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                type="text"
                className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400"
              />
              <button
                type="button"
                onClick={searchTable}
                className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center"
              >
                <SearchIcon />
              </button>
            </div>
          </div>
        </Dialog>

        {/* MASKA */}
        <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
          <div className="flex flex-col">
            <p className="text-sm text-gray-600 mb-2">{t('MaintenanceTemplateList.MaskHelp')}</p>
            <div className="flex flex-wrap gap-2 mb-2">
              <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
              <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
            </div>
            {columns.map((item, index) => {
              if (index < 2) return null;
              return (
                <div key={item.field ?? index} className="flex flex-row border border-black rounded-md p-2 m-1">
                  <MaintenanceMaskDialogCheckbox field={item.field} inputId={`mt-mask-${item.field}`} title={item.title || item.field} columnsVisible={columnsVisible} onChange={setMask} />
                  <label htmlFor={`mt-mask-${item.field}`} className={`ml-2 ${item.field === 'id' ? '' : 'cursor-pointer'}`}>{item.title}</label>
                </div>
              );
            })}
            <button type="button" onClick={saveUserMaskColumns}
              className='m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700'>{t('Common.SaveUpper')}</button>
          </div>
        </Dialog>
</div>
    </>
  );
}